import React, { FC, ReactNode } from "react";
import { Tooltip } from "@mui/material";
import { IconInfo } from "../../utils/icons";

// A form label with an info bubble: hover the icon for what the field is
// for. Shared by the Product Pages forms in product settings and video
// settings so the two explain each field the same way.

export const PDP_FIELD_HELP = {
    pdpUrl:
        "The retailer product page this row describes. The JSON-LD is anchored to this address, so changing it regenerates the markup.",
    videoFileUrl:
        "The direct video file (mp4 or m3u8) as it is served on the retailer's page for this video — the VideoObject's contentUrl, which Google needs to show the video in results. The PDP analysis fills this in when it finds this video on the page (by its known placement, or matched by duration). Enter it by hand only if the analysis can't see the player.",
    thumbnailUrl:
        "The still image Google shows for this video. Prefilled with the poster the retailer's page shows for it when the PDP analysis found one, otherwise with our own thumbnail. Set it by hand only if you want a different image; clear it to go back to the detected one.",
    uploadDate:
        "When the video was published on this page. Prefilled with our approval date; set the retailer's publish date if it differs. Clear it to go back to ours.",
    brand: "Whose page this is — the retailer for a retail listing, the supplier for its own site.",
    skus: "The SKUs sold on this page. They tag the page for API lookups by SKU and never affect scoring or the markup."
} as const;

export const FieldLabel: FC<{ children: ReactNode; help?: string; htmlFor?: string; className?: string }> = ({
    children,
    help,
    htmlFor,
    className = "block text-[12px] font-semibold text-text-2 uppercase tracking-[0.3px] mb-[7px]"
}) => (
    <label htmlFor={htmlFor} className={className}>
        <span className="inline-flex items-center gap-[5px]">
            {children}
            {help && (
                <Tooltip title={help} placement="top" arrow>
                    <span className="inline-flex text-muted hover:text-text-1 cursor-help normal-case tracking-normal">
                        <IconInfo size={13} stroke="currentColor" />
                    </span>
                </Tooltip>
            )}
        </span>
    </label>
);
